//================= [JS] ======================================
import {onBeforeUnmount, ref, watch} from 'vue';
import {useApiClient} from '@/common/api/useApiClient';
import {IndividualCoachingApi} from '@/common/api/individualCoaching/IndividualCoachingApi';
import type {ISessionRecording} from '@/types/components/individualCoaching/IIndividualCoaching';
import type {CallMessage, useCoachingCall} from '@/composables/individualCoaching/useCoachingCall';

/**
 * off → (coach) requesting → recording → stopping → off
 * off → (client) consent → recording → off
 */
export type RecordingState = 'off' | 'requesting' | 'consent' | 'recording' | 'stopping';

/** A piece every 10 s keeps uploads small and loses little if the browser dies. */
export const RECORDING_TIMESLICE_MS = 10_000;
const FRAME_RATE = 15;
const TILE_WIDTH = 640;
const TILE_HEIGHT = 360;

type Call = ReturnType<typeof useCoachingCall>;

/** The first format this browser can record: WebM (Chrome, Firefox, Edge), MP4 (Safari). */
export function recordingMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') {
    return null;
  }
  return ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'].find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

/**
 * T00EXMB3XI6N — recording a 1:1 call, only with the client's consent.
 *
 * The handshake runs over the call's data channel: the coach asks, the client
 * sees the consent dialog and, on yes, sends the consent to the API from their
 * own browser (that is the proof) and passes the recording id back. Only then
 * does the coach's browser record: both pictures side by side on a canvas,
 * both voices mixed, uploaded in pieces while the call runs. Either side can
 * stop; the client's stop also records the withdrawal.
 */
export function useCallRecording(call: Call, clientId: string, sessionId: string, occurrenceDate: string, isCoach: () => boolean) {
  const apiClient = useApiClient();

  const state = ref<RecordingState>('off');
  const recordingId = ref<string | null>(null);
  /** The client said no; the coach sees a hint. */
  const declined = ref(false);
  const failed = ref(false);
  const supported = recordingMimeType() !== null && typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype;

  let recorder: MediaRecorder | null = null;
  let sequence = 0;
  let uploads: Promise<void> = Promise.resolve();
  let teardown: (() => void) | null = null;

  //================= [HANDSHAKE] ==============================

  /** Coach: ask the client. Nothing is recorded until they agree. */
  function request(): void {
    if (!isCoach() || state.value !== 'off') {
      return;
    }
    declined.value = false;
    if (call.sendMessage({type: 'recording-request'})) {
      state.value = 'requesting';
    }
  }

  /** Client: agree. The consent goes to the API from this browser, signed in as the client. */
  async function accept(): Promise<void> {
    try {
      const response = await apiClient.post<ISessionRecording>(IndividualCoachingApi.recordingConsent(clientId, sessionId, occurrenceDate), {});
      recordingId.value = response.data.id;
      call.sendMessage({type: 'recording-consent', recordingId: response.data.id});
      state.value = 'recording';
    } catch {
      call.sendMessage({type: 'recording-declined'});
      state.value = 'off';
      failed.value = true;
    }
  }

  /** Client: refuse. The call goes on as before. */
  function decline(): void {
    call.sendMessage({type: 'recording-declined'});
    state.value = 'off';
  }

  /** Either side: stop. The client's stop is a withdrawal and is recorded as such. */
  async function stop(): Promise<void> {
    if (state.value !== 'recording' && state.value !== 'requesting') {
      return;
    }
    call.sendMessage({type: 'recording-stop'});
    if (isCoach()) {
      await stopRecorder();
    } else {
      const id = recordingId.value;
      state.value = 'off';
      recordingId.value = null;
      if (id) {
        await apiClient.post(IndividualCoachingApi.recordingAction(clientId, id, 'withdraw'), {}).catch(() => undefined);
      }
    }
  }

  function onMessage(message: CallMessage): void {
    switch (message.type) {
      case 'recording-request':
        if (!isCoach() && state.value === 'off') {
          state.value = 'consent';
        }
        break;
      case 'recording-consent':
        if (isCoach() && state.value === 'requesting' && typeof message.recordingId === 'string') {
          recordingId.value = message.recordingId;
          startRecorder();
        }
        break;
      case 'recording-declined':
        if (isCoach()) {
          state.value = 'off';
          declined.value = true;
        }
        break;
      case 'recording-stop':
        if (isCoach()) {
          void stopRecorder();
        } else {
          state.value = 'off';
          recordingId.value = null;
        }
        break;
    }
  }

  //================= [RECORDER (coach)] =======================

  function startRecorder(): void {
    const mimeType = recordingMimeType();
    if (!mimeType || !supported) {
      failRecording();
      return;
    }
    const composed = compose();
    if (!composed) {
      failRecording();
      return;
    }
    teardown = composed.teardown;
    sequence = 0;
    uploads = Promise.resolve();
    recorder = new MediaRecorder(composed.stream, {mimeType});
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        const piece = event.data;
        const number = sequence++;
        uploads = uploads.then(() => upload(number, piece, mimeType));
      }
    };
    recorder.start(RECORDING_TIMESLICE_MS);
    state.value = 'recording';
    call.sendMessage({type: 'recording-started'});
  }

  async function upload(number: number, piece: Blob, mimeType: string): Promise<void> {
    const id = recordingId.value;
    if (!id) {
      return;
    }
    const form = new FormData();
    form.append('sequence', String(number));
    form.append('mimeType', mimeType);
    form.append('chunk', piece, `${number}.part`);
    try {
      await apiClient.post(IndividualCoachingApi.recordingAction(clientId, id, 'chunks'), form, {headers: {'Content-Type': 'multipart/form-data'}});
    } catch (caught) {
      // Withdrawn on the other side, or the storage is full: stop instead of piling up pieces.
      const code = (caught as {response?: {status?: number}})?.response?.status;
      if (code === 409 || code === 413 || code === 422) {
        void stopRecorder();
      }
    }
  }

  async function stopRecorder(): Promise<void> {
    if (state.value === 'requesting') {
      state.value = 'off';
      return;
    }
    if (!recorder || state.value === 'stopping' || state.value === 'off') {
      return;
    }
    state.value = 'stopping';
    const active = recorder;
    recorder = null;
    // The last piece arrives with the stop event.
    await new Promise<void>((resolve) => {
      active.onstop = () => resolve();
      if (active.state === 'inactive') {
        resolve();
      } else {
        active.stop();
      }
    });
    teardown?.();
    teardown = null;
    await uploads;
    const id = recordingId.value;
    if (id) {
      await apiClient.post(IndividualCoachingApi.recordingAction(clientId, id, 'finish'), {}).catch(() => undefined);
    }
    recordingId.value = null;
    state.value = 'off';
  }

  function failRecording(): void {
    failed.value = true;
    state.value = 'off';
    call.sendMessage({type: 'recording-stop'});
  }

  /**
   * Both pictures side by side (a shared screen, from either side, instead of
   * that side's camera), both voices mixed. Drawn with a timer instead of
   * requestAnimationFrame, which stops when the coach looks at another tab.
   */
  function compose(): {stream: MediaStream; teardown: () => void} | null {
    const AudioContextClass = window.AudioContext;
    const canvas = document.createElement('canvas');
    canvas.width = TILE_WIDTH * 2;
    canvas.height = TILE_HEIGHT;
    const context2d = canvas.getContext('2d');
    if (!context2d || !AudioContextClass) {
      return null;
    }
    const remoteVideo = hiddenVideo(call.remoteStream.value);
    const localVideo = hiddenVideo(call.localStream.value);
    const screenVideo = () => {
      const track = call.currentScreenTrack();
      return track ? hiddenVideo(new MediaStream([track])) : null;
    };
    let screen = screenVideo();
    // T00O4VGPVISQ — the other side's screen now comes next to their camera.
    let remoteScreen: HTMLVideoElement | null = null;
    let remoteScreenStream: MediaStream | null = null;
    const draw = (video: HTMLVideoElement | null, x: number) => {
      context2d.fillStyle = '#111';
      context2d.fillRect(x, 0, TILE_WIDTH, TILE_HEIGHT);
      if (video && video.videoWidth > 0) {
        const scale = Math.min(TILE_WIDTH / video.videoWidth, TILE_HEIGHT / video.videoHeight);
        const width = video.videoWidth * scale;
        const height = video.videoHeight * scale;
        context2d.drawImage(video, x + (TILE_WIDTH - width) / 2, (TILE_HEIGHT - height) / 2, width, height);
      }
    };
    const timer = setInterval(() => {
      if (call.sharing.value && !screen) {
        screen = screenVideo();
      } else if (!call.sharing.value && screen) {
        screen.srcObject = null;
        screen = null;
      }
      const sharedByOther = call.remoteScreenStream?.value ?? null;
      if (sharedByOther !== remoteScreenStream) {
        if (remoteScreen) {
          remoteScreen.srcObject = null;
        }
        remoteScreenStream = sharedByOther;
        remoteScreen = hiddenVideo(sharedByOther);
      }
      draw(remoteScreen ?? (call.remoteState.value.videoOff ? null : remoteVideo), 0);
      draw(screen ?? (call.videoOff.value ? null : localVideo), TILE_WIDTH);
    }, 1000 / FRAME_RATE);

    const audio = new AudioContextClass();
    const destination = audio.createMediaStreamDestination();
    for (const stream of [call.remoteStream.value, call.localStream.value]) {
      if (stream && stream.getAudioTracks().length > 0) {
        audio.createMediaStreamSource(new MediaStream(stream.getAudioTracks())).connect(destination);
      }
    }
    const stream = new MediaStream([...canvas.captureStream(FRAME_RATE).getVideoTracks(), ...destination.stream.getAudioTracks()]);

    return {
      stream,
      teardown: () => {
        clearInterval(timer);
        stream.getTracks().forEach((track) => track.stop());
        void audio.close().catch(() => undefined);
        [remoteVideo, localVideo, screen, remoteScreen].forEach((video) => {
          if (video) {
            video.srcObject = null;
          }
        });
      },
    };
  }

  function hiddenVideo(stream: MediaStream | null): HTMLVideoElement | null {
    if (!stream) {
      return null;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    void video.play().catch(() => undefined);
    return video;
  }

  //================= [LIFECYCLE] ==============================

  const unsubscribe = call.onMessage(onMessage);
  // Hanging up or losing the other side ends a running recording properly.
  watch(call.status, (status) => {
    if (status === 'ended' || status === 'waiting') {
      if (isCoach()) {
        void stopRecorder();
      } else {
        state.value = 'off';
      }
    }
  });
  onBeforeUnmount(() => {
    unsubscribe();
    void stopRecorder();
  });

  return {state, recordingId, declined, failed, supported, request, accept, decline, stop, onMessage};
}
